import { Suspense } from "react";

import {
  createBrowserRouter,
  Outlet,
  Navigate,
  RouterProvider,
  Link,
  type RouteObject,
} from "react-router-dom";

import PageLoader from "@/components/common/PageLoader";
import { AppShell } from "@/components/layout";
import { ROUTES } from "@/constants";
import AuthGuard from "@/lib/router/guards/AuthGuard";
import GuestGuard from "@/lib/router/guards/GuestGuard";
import RoleGuard from "@/lib/router/guards/RoleGuard";
import {
  CalendarPage,
  ClusterManagementPage,
  DashboardPage,
  EventDetailPage,
  EventsPage,
  ForgotPasswordPage,
  LoginPage,
  SquadBuilderPage,
  RegisterPage,
  SettingsPage,
  CompaniesPage,
  FixturesPage,
  TeamsPage,
  ProductsPage,
  RequestManagementPage,
  SpacesPage,
  TransactionsPage,
  InvitedPage,
  CartPage,
  AttendeesPage,
  OrdersHistoryPage,
  RegisterRequestsPage,
  LeagueManagementPage,
  ContentsPage,
  LabelContentRoute,
  LeagueOnboardingPage,
  PerformanceRatingPage,
} from "@/lib/router/lazyPages";
import CouponsPage from "@/pages/admin/coupon/Coupon";
import { getEventBasePath } from "@/lib/auth/session";

// ─── Auth layout (no sidebar) ─────────────────────────────────────
const AuthLayout = () => (
  <Suspense fallback={null}>
    <Outlet />
  </Suspense>
);

// ─── Route tree ───────────────────────────────────────────────────
/**
 * Route structure:
 *
 * /
 * ├── (guest) /auth/*          → GuestGuard → AuthLayout
 * │   ├── /auth/login
 * │   ├── /auth/register
 * │   └── /auth/forgot-password
 * │
 * └── (protected) /*           → AuthGuard → AppLayout
 *     ├── /                    → DashboardPage
 *     ├── /events              → EventsPage
 *     ├── /events/:id          → EventDetailPage
 *     ├── /teams               → TeamsPage
 *     └── /settings            → RoleGuard(admin|manager) → SettingsPage
 *         └── /settings/profile → ProfilePage
 */
const basename = getEventBasePath();

const routes: RouteObject[] = [
  // ── Public / auth routes ──────────────────────────────────────
  {
    element: <GuestGuard />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: ROUTES.LOGIN, element: <LoginPage /> },
          { path: ROUTES.REGISTER, element: <RegisterPage /> },
          { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
        ],
      },
    ],
  },

  // ── Protected app routes ──────────────────────────────────────
  {
    element: <AuthGuard />,
    children: [
      {
        element: (
          <AppShell>
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </AppShell>
        ),
        children: [
          { path: "/", element: <Navigate to={ROUTES.DASHBOARD} replace /> },
          { path: ROUTES.DASHBOARD, element: <DashboardPage /> },
          { path: ROUTES.EVENTS, element: <EventsPage /> },
          { path: ROUTES.EVENT_DETAIL, element: <EventDetailPage /> },
          { path: ROUTES.TEAMS, element: <TeamsPage /> },
          { path: ROUTES.CALENDAR, element: <CalendarPage /> },
          { path: ROUTES.COMPANIES, element: <CompaniesPage /> },
          // Legacy club content paths (keep for compatibility with old URLs)
          { path: "club-calendar", element: <CalendarPage /> },
          { path: "club-members", element: <AttendeesPage /> },
          { path: "club-announcements", element: <ContentsPage /> },
          { path: "club-shop", element: <ContentsPage /> },
          { path: "club-policies", element: <ContentsPage /> },
          { path: "team-1", element: <ContentsPage /> },
          // Direct fixtures & orders routes (some tenants use these paths)
          { path: "fixtures", element: <FixturesPage /> },
          { path: "orders", element: <OrdersHistoryPage /> },
          // Club-specific pages removed from top-level routing. Use the
          // generic content route `/content/:pageSlug?` which is handled by
          // `ContentsPage` and renders backend-driven content.
          { path: ROUTES.CART, element: <CartPage /> },
          // { path: ROUTES.FIXTURES, element: <FixturesPage /> },
          { path: ROUTES.MEMBERS, element: <AttendeesPage /> },
          { path: ROUTES.ORDERS_HISTORY, element: <OrdersHistoryPage /> },
          { path: ROUTES.CONTENTS, element: <ContentsPage /> },
          // Generic content page route (optional page slug)
          { path: ROUTES.CONTENT_PAGE, element: <ContentsPage /> },
          // Also accept label-derived slugs such as "club-suppliers" and route
          // them through a dedicated label router which will render the
          // correct component (e.g. COMPANIES -> CompaniesPage) while
          // preserving the visible label in the URL.
          { path: ":pageSlug", element: <LabelContentRoute /> },
          // { path: "league-onboarding", element: <LeagueOnboardingPage /> },
          {
            path: ROUTES.PERFORMANCE_RATING,
            element: <PerformanceRatingPage />,
          },
          { path: ROUTES.LEAGUE_ONBOARDING, element: <LeagueOnboardingPage /> },

          // Settings subtree — admin/manager only
          {
            element: (
              <RoleGuard
                allowedRoles={[
                  "SUPER_ADMIN",
                  "MASTER_ADMIN",
                  "ADMIN",
                  "MANAGER",
                ]}
              />
            ),
            children: [
              { path: ROUTES.SETTINGS, element: <SettingsPage /> },
              { path: ROUTES.PROFILE, element: <SquadBuilderPage /> },
              {
                path: ROUTES.CLUSTER_MANAGEMENT,
                element: <ClusterManagementPage />,
              },
              { path: ROUTES.SQUAD_BUILDER, element: <SquadBuilderPage /> },
              { path: ROUTES.PRODUCTS, element: <ProductsPage /> },
              {
                path: ROUTES.REQUEST_MANAGEMENT,
                element: <RequestManagementPage />,
              },
              {
                path: ROUTES.INVITED,
                element: <InvitedPage />,
              },
              {
                path: ROUTES.SPACES,
                element: <SpacesPage />,
              },
              {
                path: ROUTES.TRANSACTIONS,
                element: <TransactionsPage />,
              },
              {
                path: ROUTES.COUPONS,
                element: <CouponsPage />,
              },
              {
                path: ROUTES.REGISTER_REQUESTS,
                element: <RegisterRequestsPage />,
              },
              {
                path: ROUTES.LEAGUE_MANAGEMENT,
                element: <LeagueManagementPage />,
              },
              // League Management menu template resolves to /content/<label-slug>.
              {
                path: "/content/league-management",
                element: <LeagueManagementPage />,
              },
            ],
          },
        ],
      },
    ],
  },

  // ── Catch-all 404 ─────────────────────────────────────────────
  {
    path: "*",
    element: (
      <div className="flex h-screen flex-col items-center justify-center gap-2">
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-50">
          404
        </h1>
        <p className="text-neutral-500">
          Page not found. Please check the URL and make sure the event exists.
          If you believe this is an error, please contact support.
        </p>
        <Link to={ROUTES.DASHBOARD} className="text-primary-500 hover:underline">
          Go to dashboard
        </Link>
      </div>
    ),
  },
];

export const router = createBrowserRouter(routes, {
  basename,
  // Future flags — adopts React Router v7 behaviours early
  future: {
    v7_normalizeFormMethod: true,
  },
});

// Default export a stable React component for React Refresh
export default function AppRouter() {
  return <RouterProvider router={router} />;
}
